import { FC, useMemo, useState } from 'react';
import { Box, Divider, IconButton, Popover, Tooltip, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { ChartScatterIcon, InfoIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import { DomainResultViewModel } from '../../types';

// ── Constants ─────────────────────────────────────────────────────────────────

const SVG_W = 800;
const CHART_PAD = { top: 24, right: 24, bottom: 44, left: 52 };

// ── Types ─────────────────────────────────────────────────────────────────────

interface DomainRiskMatrixChartProps {
  domainResults: DomainResultViewModel[];
  height?: number;
}

interface QuadrantDef {
  x: number;
  y: number;
  w?: number;
  h?: number;
  label: string;
  color: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const DomainRiskMatrixChart: FC<DomainRiskMatrixChartProps> = observer(
  ({ domainResults, height = 300 }) => {
    const theme = useTheme();
    const { i18n } = useTrans();
    const { isRtl } = useDirection();
    const lang = i18n.language === 'ar' ? 'ar' : 'en';

    const [infoAnchor, setInfoAnchor] = useState<HTMLButtonElement | null>(null);

    const innerW = SVG_W - CHART_PAD.left - CHART_PAD.right;
    const innerH = height - CHART_PAD.top - CHART_PAD.bottom;
    const midX   = CHART_PAD.left + innerW / 2;
    const midY   = CHART_PAD.top  + innerH / 2;

    const maxGaps   = useMemo(() => Math.max(...domainResults.map((d) => d.gaps), 1),          [domainResults]);
    const maxWeight = useMemo(() => Math.max(...domainResults.map((d) => d.weight ?? 1), 1),   [domainResults]);

    const toX = (score: number) => CHART_PAD.left + (score / 100) * innerW;
    const toY = (gaps: number) => CHART_PAD.top + (1 - gaps / maxGaps) * innerH;
    const toR = (weight: number) => Math.max(6, Math.min(22, 6 + (weight / maxWeight) * 16));

    const xTicks = [0, 25, 50, 75, 100];
    const yTicks = useMemo(() => {
      const step = Math.ceil(maxGaps / 4);
      const ticks = [0, step, step * 2, step * 3, maxGaps];
      return [...new Set(ticks)];
    }, [maxGaps]);

    // Quadrant definitions — Critical = low-score (left) + many-gaps (top)
    const quadrants: QuadrantDef[] = useMemo(() => {
      const qLabels = lang === 'ar'
        ? { critical: 'حرج', watch: 'مراقبة', improve: 'تحسين', good: 'جيد' }
        : { critical: 'Critical', watch: 'Watch', improve: 'Improve', good: 'Good' };

      return [
        { x: CHART_PAD.left, y: CHART_PAD.top, w: innerW / 2, h: innerH / 2, label: qLabels.critical, color: theme.palette.error.main },
        { x: midX,           y: CHART_PAD.top, w: innerW / 2, h: innerH / 2, label: qLabels.watch,    color: theme.palette.warning.main },
        { x: CHART_PAD.left, y: midY,          w: innerW / 2, h: innerH / 2, label: qLabels.improve,  color: theme.palette.info.main },
        { x: midX,           y: midY,          w: innerW / 2, h: innerH / 2, label: qLabels.good,     color: theme.palette.success.main },
      ];
    }, [lang, midX, midY, innerW, innerH, theme.palette]);

    // Bilingual text
    const labels = {
      title:         lang === 'ar' ? 'مصفوفة المخاطر'                                    : 'Risk Matrix',
      subtitle:      lang === 'ar' ? 'درجة النضج مقابل عدد الفجوات'                      : 'Maturity score vs. gap count',
      howToRead:     lang === 'ar' ? 'كيفية قراءة هذا الرسم'                             : 'How to read this chart',
      xAxisTitle:    lang === 'ar' ? '← درجة النضج'                                      : 'Maturity Score →',
      yAxisTitle:    lang === 'ar' ? 'الفجوات ↑'                                         : 'Gaps ↑',
      noData:        lang === 'ar' ? 'لا توجد بيانات للنطاقات'                            : 'No domain data',
      notEvaluated:  lang === 'ar' ? 'لم يُقيَّم'                                         : 'Not evaluated',
      bubbleSizeTip: lang === 'ar' ? 'حجم الفقاعة = وزن النطاق'                          : 'Bubble size = domain weight',
      maturityLabel: (s: number) => lang === 'ar' ? `النضج: ${s}%` : `Maturity: ${s}%`,
      gapsLabel:     (g: number) => lang === 'ar' ? `الفجوات: ${g}` : `Gaps: ${g}`,
      weightLabel:   (w: number) => lang === 'ar' ? `الوزن: ${w}` : `Weight: ${w}`,
    };

    const infoItems = lang === 'ar'
      ? [
          { label: 'المحور الأفقي (→)',   desc: 'درجة الجاهزية. النطاقات على اليمين أكثر جاهزية.' },
          { label: 'المحور العمودي (↑)',   desc: 'عدد الفجوات المفتوحة. كلما ارتفع الموقع، زادت الفجوات.' },
          { label: 'حجم الفقاعة',         desc: 'الوزن التنظيمي. الفقاعات الأكبر تؤثر أكثر على نتيجتك.' },
          { label: 'المنطقة الحرجة',       desc: 'جاهزية منخفضة + فجوات كثيرة. هذه أعلى أولوياتك.' },
          { label: 'المنطقة الجيدة',       desc: 'جاهزية عالية + فجوات قليلة. هذه النطاقات تعمل بكفاءة.' },
        ]
      : [
          { label: 'X-axis (→)',           desc: 'Readiness score. Domains further right are more prepared.' },
          { label: 'Y-axis (↑)',           desc: 'Number of open gaps. Higher = more unresolved issues.' },
          { label: 'Bubble size',          desc: 'Regulatory weight. Larger bubbles have more impact on your score.' },
          { label: 'Critical zone',        desc: 'Low readiness AND many gaps — highest priority domains.' },
          { label: 'Ready zone',           desc: 'High readiness AND few gaps — performing well.' },
        ];

    if (domainResults.length === 0) {
      return (
        <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="textSM_regular" sx={{ color: 'text.tertiary' }}>
            {labels.noData}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {/* ── Header ── */}
        <Box sx={{ px: 2.5, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box sx={{ mt: 0.25, color: 'error.light', flexShrink: 0 }}>
              <ChartScatterIcon size={18} />
            </Box>
            <Box>
              <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', display: 'block' }}>
                {labels.title}
              </Typography>
              <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', display: 'block' }}>
                {labels.subtitle}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={(e) => setInfoAnchor(e.currentTarget)}
            sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
          >
            <InfoIcon size={16} />
          </IconButton>
        </Box>

        <Divider sx={{ mx: 2.5, borderColor: 'grey.100', mb: 0.5 }} />

        {/* ── Info popover ── */}
        <Popover
          open={Boolean(infoAnchor)}
          anchorEl={infoAnchor}
          onClose={() => setInfoAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
          slotProps={{
            paper: {
              sx: {
                maxWidth: 300,
                p: 2,
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                border: `1px solid ${theme.palette.grey[200]}`,
              },
            },
          }}
        >
          <Typography variant="textSM_semiBold" sx={{ display: 'block', mb: 1 }}>
            {labels.howToRead}
          </Typography>
          {infoItems.map(({ label, desc }) => (
            <Box key={label} sx={{ mb: 1.25 }}>
              <Typography variant="textXS_semiBold" sx={{ color: 'text.primary', display: 'block' }}>{label}</Typography>
              <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>{desc}</Typography>
            </Box>
          ))}
        </Popover>

        {/* ── SVG ── */}
        {/* Pinned LTR: the SVG's text-anchor placement assumes a
            left-to-right coordinate flow. */}
        <NoFlipBox mode="ltr" sx={{ px: 1 }}>
          <svg
            viewBox={`0 0 ${SVG_W} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ width: '100%', height: 'auto', maxHeight: height, display: 'block' }}
          >
            {/* Quadrant backgrounds */}
            {quadrants.map((q, i) => (
              <rect key={i} x={q.x} y={q.y} width={q.w ?? 0} height={q.h ?? 0} fill={q.color} fillOpacity={0.05} />
            ))}

            {/* Quadrant labels */}
            {quadrants.map((q, i) => (
              <text
                key={i}
                x={q.x + 6} y={q.y + 14}
                fontSize={9.5} fontWeight={700}
                fill={q.color} opacity={0.55}
                textAnchor="start"
                style={{ fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: 1 }}
              >
                {q.label}
              </text>
            ))}

            {/* Vertical grid lines */}
            {xTicks.map((v) => (
              <line
                key={v}
                x1={toX(v)} y1={CHART_PAD.top}
                x2={toX(v)} y2={CHART_PAD.top + innerH}
                stroke={theme.palette.grey[200]}
                strokeWidth={v === 50 ? 1.5 : 0.75}
                strokeDasharray={v === 50 ? '4 3' : '2 3'}
              />
            ))}

            {/* Horizontal grid lines */}
            {yTicks.map((v) => (
              <line
                key={v}
                x1={CHART_PAD.left} y1={toY(v)}
                x2={CHART_PAD.left + innerW} y2={toY(v)}
                stroke={theme.palette.grey[200]}
                strokeWidth={v === Math.round(maxGaps / 2) ? 1.5 : 0.75}
                strokeDasharray="2 3"
              />
            ))}

            {/* X-axis tick labels */}
            {xTicks.map((v) => (
              <text
                key={v}
                x={toX(v)} y={CHART_PAD.top + innerH + 14}
                textAnchor="middle"
                fontSize={9} fill={theme.palette.text.secondary}
                style={{ fontFamily: 'inherit' }}
              >
                {v}%
              </text>
            ))}

            {/* Y-axis tick labels */}
            {yTicks.map((v) => (
              <text
                key={v}
                x={CHART_PAD.left - 8} y={toY(v) + 3.5}
                textAnchor="end"
                fontSize={9} fill={theme.palette.text.secondary}
                style={{ fontFamily: 'inherit' }}
              >
                {v}
              </text>
            ))}

            {/* Axis titles */}
            <text
              x={CHART_PAD.left + innerW / 2} y={height - 4}
              textAnchor="middle"
              fontSize={9.5} fontWeight={500}
              fill={theme.palette.text.secondary}
              style={{ fontFamily: 'inherit' }}
            >
              {labels.xAxisTitle}
            </text>
            <text
              transform={`translate(11, ${CHART_PAD.top + innerH / 2}) rotate(-90)`}
              textAnchor="middle"
              fontSize={9.5} fontWeight={500}
              fill={theme.palette.text.secondary}
              style={{ fontFamily: 'inherit' }}
            >
              {labels.yAxisTitle}
            </text>

            {/* Chart border */}
            <rect
              x={CHART_PAD.left} y={CHART_PAD.top}
              width={innerW} height={innerH}
              fill="none"
              stroke={theme.palette.grey[300]}
              strokeWidth={1}
            />

            {/* Bubbles */}
            {domainResults.map((d, idx) => {
              const evaluated = d.isEvaluated === true;
              const cx    = toX(evaluated ? d.maturityScore : 0);
              const cy    = toY(evaluated ? d.gaps : 0);
              const r     = toR(d.weight ?? 1);
              const color = evaluated
                ? (d.maturityConfig?.color?.hex ?? theme.palette.grey[500])
                : theme.palette.grey[400];
              const score = Math.round(d.maturityScore);

              return (
                <Tooltip
                  key={idx}
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="textXS_semiBold" sx={{ display: 'block', mb: 0.25 }}>
                        {d.domainName}
                      </Typography>
                      {evaluated ? (
                        <>
                          <Typography variant="textXS_regular" sx={{ display: 'block' }}>{labels.maturityLabel(score)}</Typography>
                          <Typography variant="textXS_regular" sx={{ display: 'block' }}>{labels.gapsLabel(d.gaps)}</Typography>
                          <Typography variant="textXS_regular" sx={{ display: 'block' }}>{labels.weightLabel(d.weight ?? 1)}</Typography>
                        </>
                      ) : (
                        <Typography variant="textXS_regular" sx={{ display: 'block', color: 'text.disabled' }}>
                          {labels.notEvaluated}
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <g style={{ cursor: 'pointer' }}>
                    <circle
                      cx={cx} cy={cy} r={r}
                      fill={color} fillOpacity={0.2}
                      stroke={color} strokeOpacity={0.7} strokeWidth={1.5}
                    />
                    {r >= 12 && (
                      <text
                        x={cx} y={cy + 3.5}
                        textAnchor="middle"
                        fontSize={8.5} fontWeight={700}
                        fill={color}
                        style={{ fontFamily: 'inherit', pointerEvents: 'none' }}
                      >
                        {evaluated ? score : '—'}
                      </text>
                    )}
                  </g>
                </Tooltip>
              );
            })}
          </svg>

          {/* Size legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, mt: 0.5 }}>
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', fontSize: '0.65rem' }}>
              {labels.bubbleSizeTip}
            </Typography>
          </Box>
        </NoFlipBox>
      </Box>
    );
  },
);
